import type { TableOrder, VendorShow, VendorTableKind } from "@hobbyshopify/backend-client";
import { formatAmount } from "./money";

/** What paying for a held table takes: the table's price, the fees on top, and the total. */
export function tableCharge(order: Pick<TableOrder, "priceCents" | "amountCents">): {
  priceCents: number;
  feesCents: number;
  totalCents: number;
} {
  return {
    priceCents: order.priceCents,
    feesCents: order.amountCents - order.priceCents,
    totalCents: order.amountCents,
  };
}

/** The hold's clock, "4:59", urgent in its last minute; null once it has run out. */
export function holdClock(leftMs: number): { text: string; urgent: boolean } | null {
  if (leftMs <= 0) return null;
  const seconds = Math.ceil(leftMs / 1000);
  return {
    text: `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`,
    urgent: leftMs < 60_000,
  };
}

/** "3 of 10 left", or that every table is taken. */
export function tablesLeft(kind: Pick<VendorTableKind, "tables" | "left">): string {
  return kind.left === 0 ? "All taken" : `${kind.left} of ${kind.tables} left`;
}

/** The tables the shop holds at the show: "Table 7 (Wall)". Null while it holds none. */
export function heldTables(show: Pick<VendorShow, "myTables" | "kinds">): string | null {
  if (show.myTables.length === 0) return null;
  return show.myTables
    .map((table) => {
      const kind = show.kinds.find((k) => k.id === table.kindId);
      return kind ? `Table ${table.label} (${kind.name})` : `Table ${table.label}`;
    })
    .join(", ");
}

/** The host's notes on the tables the shop paid for: "From Host Cards, for table 7". */
export function hostNotes(
  show: Pick<VendorShow, "myTables" | "host">,
): { id: string; heading: string; text: string }[] {
  const host = show.host.name ?? "the host";
  return show.myTables.flatMap((table) =>
    table.privateDescription
      ? [{ id: table.id, heading: `From ${host}, for table ${table.label}`, text: table.privateDescription }]
      : [],
  );
}

/**
 * One table per shop per show for now: a kind is offered while the show is on sale, the
 * host takes payments, the shop holds no table there and pays for none, and one is left.
 */
export function canTakeTable(
  show: Pick<VendorShow, "onSale" | "takesPayments" | "myTables" | "myOrder">,
  kind: Pick<VendorTableKind, "left">,
): boolean {
  return show.onSale && show.takesPayments && show.myOrder === null && show.myTables.length === 0 && kind.left > 0;
}

/**
 * What became of the shop's money at the show, newest first. A refund still owed is
 * always said. The latest one sent is said while it still matters: the show is
 * cancelled, or the shop holds no table there.
 */
export function refundNotes(
  show: Pick<VendorShow, "cancelledAt" | "myTables" | "myRefunds">,
): { id: string; text: string }[] {
  let sentSaid = Boolean(!show.cancelledAt && show.myTables.length > 0);
  return show.myRefunds.flatMap((refund) => {
    const why = refund.refundReason ? ` ${refund.refundReason}` : "";
    const amount = formatAmount(refund.amountCents, refund.currency);
    const note = (text: string) => [{ id: refund.id, text }];
    if (refund.status === "FAILED") {
      return note(`${amount} is owed back to you.${why} If it does not arrive soon, ask the host.`);
    }
    if (sentSaid) return [];
    sentSaid = true;
    if (refund.amountCents === 0) return note(`Table ${refund.tableLabel} was given back.${why}`);
    const on = refund.refundedAt ? ` on ${refund.refundedAt.toLocaleDateString()}` : "";
    return note(`${amount} was sent back${on}.${why}`);
  });
}
